import classNames from "classnames";
import moment from "moment";
import React from "react";
import {
  Control,
  Controller,
  FieldErrors,
  UseFormRegister,
} from "react-hook-form";

import { StyledDatePicker } from "../../../../../components/datepicker/datepicker";
import { SettingsInput } from "../../../../../components/inputs/SettingsInput";
import { FieldLabel } from "../../../../../components/newUI/text";
import {
  ADD_WORK_INPUT_CLASSES,
  SHOW_BUDGETED_TIME,
} from "../model/addWork.constants";
import { AddWorkFormData } from "../model/addWork.types";

// Shown as "Aug 20, 2026" (Roadmap 1813).
const DATE_FORMAT = "MMM dd, yyyy";

const toPickerDate = (value: string): Date | null =>
  value && moment(value).isValid() ? moment(value).toDate() : null;

// A cleared date must be picked again instead of quietly using the old one
// (Roadmap 1804).
const requiredDate = (message: string) => (value: string) =>
  (Boolean(value) && moment(value).isValid()) || message;

type ScheduleBudgetSectionProps = {
  control: Control<AddWorkFormData>;
  errors: FieldErrors<AddWorkFormData>;
  register: UseFormRegister<AddWorkFormData>;
};

export const ScheduleBudgetSection: React.FC<ScheduleBudgetSectionProps> = ({
  control,
  errors,
  register,
}) => {
  return (
    <div
      className={classNames(
        "grid gap-4",
        SHOW_BUDGETED_TIME
          ? "grid-cols-[minmax(0,2fr)_minmax(0,2fr)_minmax(0,1fr)]"
          : "grid-cols-2",
      )}>
      <div>
        <FieldLabel label={"Start Date"} required />
        <Controller
          name={"startDate"}
          control={control}
          rules={{ validate: requiredDate("Start date is required") }}
          render={({ field: { value, onChange } }) => (
            <StyledDatePicker
              value={toPickerDate(value)}
              onChange={(date) => onChange(moment(date).toISOString())}
              onClear={() => onChange("")}
              format={DATE_FORMAT}
              placeholder={"Select a date"}
              size={"md"}
              acceptTypedDates
              showCalendarIcon
            />
          )}
        />
        {errors?.startDate?.message && (
          <div className={"mt-2 text-sm font-bold text-error"}>
            {errors.startDate.message}
          </div>
        )}
      </div>

      <div>
        <FieldLabel label={"Due Date"} required />
        <Controller
          name={"dueDate"}
          control={control}
          rules={{ validate: requiredDate("Due date is required") }}
          render={({ field: { value, onChange } }) => (
            <StyledDatePicker
              value={toPickerDate(value)}
              onChange={(date) => onChange(moment(date).toISOString())}
              onClear={() => onChange("")}
              format={DATE_FORMAT}
              placeholder={"Select a date"}
              size={"md"}
              acceptTypedDates
              showCalendarIcon
            />
          )}
        />
        {errors?.dueDate?.message && (
          <div className={"mt-2 text-sm font-bold text-error"}>
            {errors.dueDate.message}
          </div>
        )}
      </div>

      <div>
        <FieldLabel
          label={"Budget"}
          infoTooltip={"Hours budgeted for this work item"}
        />
        <div className={"flex w-full items-center gap-2"}>
          <SettingsInput
            {...register("budgetedTime", {
              pattern: {
                value: /^\d*$/,
                message: "Only numbers are allowed",
              },
            })}
            type={"number"}
            min={0}
            step={1}
            inputMode={"numeric"}
            variant={"form"}
            size={"md"}
            containerClasses={"min-w-0 flex-1"}
            inputClasses={classNames(
              "w-full text-right",
              ADD_WORK_INPUT_CLASSES,
            )}
            isError={!!errors?.budgetedTime}
          />
          <span className={"text-xs "}>hrs</span>
        </div>
        {errors?.budgetedTime?.message && (
          <div className={"mt-2 text-sm font-bold text-error"}>
            {errors?.budgetedTime?.message}
          </div>
        )}
      </div>
    </div>
  );
};
